import React, { useState, useRef, useEffect } from "react";
import "./DossierPreview.css";
import { useConstants } from "../../constant/constantProvider";
import Tooltip from "../Tooltip/Tooltip";

// React PDF
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// Assets
import downloadIcon from "../../assets/images/down1.png";
import fileIcon from "../../assets/images/file-icon.svg";
import promptManagerIcon from "../../assets/images/prompt-manager-icon.svg";

// Configure worker for react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Live PDF Viewer Subcomponent using react-pdf (0 vulnerabilities, pure React state)
const LivePdfViewer = ({ fileUrl }) => {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoomScale, setZoomScale] = useState(1.0);
  const [containerWidth, setContainerWidth] = useState(null);
  const pageRefs = useRef([]);
  const containerRef = useRef(null);
  const isScrollingProgrammatically = useRef(false);
  const scrollTimeoutRef = useRef(null);

  const MIN_ZOOM = 0.4;
  const MAX_ZOOM = 2.0;
  const ZOOM_STEP = 0.15;

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const clientW = containerRef.current.clientWidth;
        if (clientW > 60) {
          setContainerWidth(clientW - 40);
        }
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const handleZoomIn = () => {
    setZoomScale((prev) => Math.min(Number((prev + ZOOM_STEP).toFixed(2)), MAX_ZOOM));
  };

  const handleZoomOut = () => {
    setZoomScale((prev) => Math.max(Number((prev - ZOOM_STEP).toFixed(2)), MIN_ZOOM));
  };

  const handleResetZoom = () => {
    setZoomScale(1.0);
  };

  const scrollToPage = (pageNum) => {
    const container = containerRef.current;
    const el = pageRefs.current[pageNum - 1];
    if (!el || !container) return;

    isScrollingProgrammatically.current = true;
    setCurrentPage(pageNum);

    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const targetScrollTop = container.scrollTop + (elRect.top - containerRect.top) - 10;

    container.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: "smooth",
    });

    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingProgrammatically.current = false;
    }, 500);
  };

  const handlePrevPage = () => {
    if (currentPage <= 1) return;
    const prev = currentPage - 1;
    scrollToPage(prev);
  };

  const handleNextPage = () => {
    if (!numPages || currentPage >= numPages) return;
    const next = currentPage + 1;
    scrollToPage(next);
  };

  const onDocumentLoadSuccess = ({ numPages: total }) => {
    setNumPages(total);
    setCurrentPage(1);
    pageRefs.current = [];
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
      const clientW = containerRef.current.clientWidth;
      if (clientW > 60) {
        setContainerWidth(clientW - 40);
      }
    }
  };

  const onDocumentLoadError = (err) => {
    console.error("❌ [LivePdfViewer] Error loading PDF document:", err);
  };

  const handleScroll = () => {
    if (isScrollingProgrammatically.current) return;
    const container = containerRef.current;
    if (!container || !pageRefs.current.length) return;

    const containerRect = container.getBoundingClientRect();
    const containerMid = containerRect.top + containerRect.height / 2;

    let closestPage = 1;
    let minDistance = Infinity;

    pageRefs.current.forEach((el, index) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const pageMid = rect.top + rect.height / 2;
      const dist = Math.abs(pageMid - containerMid);
      if (dist < minDistance) {
        minDistance = dist;
        closestPage = index + 1;
      }
    });

    setCurrentPage((prev) => (prev !== closestPage ? closestPage : prev));
  };

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  const zoomPercent = Math.round(zoomScale * 100);

  return (
    <div className="dossier-live-pdf-wrapper">
      {/* Floating Minimalist Zoom Controls in Top-Right */}
      <div className="dossier-zoom-controls">
        <Tooltip tooltipText="Zoom Out" tooltipPosition="bottom">
          <button
            type="button"
            className={`dossier-zoom-btn ${zoomScale <= MIN_ZOOM ? "disabled-button" : ""}`}
            onClick={handleZoomOut}
            disabled={zoomScale <= MIN_ZOOM}
            aria-label="Zoom Out"
          >
            −
          </button>
        </Tooltip>
        {zoomPercent !== 100 ? (
          <Tooltip tooltipText="Reset Zoom (100%)" tooltipPosition="bottom">
            <span
              className="dossier-zoom-percent clickable"
              onClick={handleResetZoom}
              role="button"
              tabIndex={0}
            >
              {zoomPercent}%
            </span>
          </Tooltip>
        ) : (
          <span className="dossier-zoom-percent">{zoomPercent}%</span>
        )}
        <div className="dossier-zoom-divider" />
        <Tooltip tooltipText="Zoom In" tooltipPosition="bottom">
          <button
            type="button"
            className={`dossier-zoom-btn ${zoomScale >= MAX_ZOOM ? "disabled-button" : ""}`}
            onClick={handleZoomIn}
            disabled={zoomScale >= MAX_ZOOM}
            aria-label="Zoom In"
          >
            +
          </button>
        </Tooltip>
      </div>

      <div
        className="dossier-pdf-container"
        ref={containerRef}
        onScroll={handleScroll}
      >
        <Document
          file={fileUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className="dossier-doc-skeleton-container">
              <div className="dossier-doc-skeleton-page">
                <div className="dossier-skeleton-shimmer skeleton-doc-top-bar" />
                <div className="dossier-skeleton-shimmer skeleton-doc-banner" />
                <div className="dossier-skeleton-shimmer skeleton-doc-line width-100" />
                <div className="dossier-skeleton-shimmer skeleton-doc-line width-90" />
                <div className="dossier-skeleton-shimmer skeleton-doc-line width-75" />
                <div className="dossier-skeleton-shimmer skeleton-doc-footer-block" />
              </div>
            </div>
          }
          error={
            <div className="dossier-no-preview">
              <p>Failed to load PDF preview</p>
            </div>
          }
        >
          {Array.from(new Array(numPages || 0), (_, index) => (
            <div
              key={`page_${index + 1}`}
              className="dossier-pdf-page-wrapper"
              ref={(el) => (pageRefs.current[index] = el)}
            >
              <Page
                pageNumber={index + 1}
                scale={zoomScale}
                width={containerWidth ? Math.min(containerWidth, 850) : undefined}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </div>
          ))}
        </Document>
      </div>

      {/* Bottom Page Navigation Bar */}
      {numPages && (
        <div className="dossier-page-nav-bar">
          <button
            className={`dossier-page-nav-btn ${currentPage <= 1 ? "disabled-button" : ""}`}
            onClick={handlePrevPage}
            disabled={currentPage <= 1}
            aria-label="Previous Page"
          >
            &#8249;
          </button>
          <span className="dossier-page-nav-label">
            {currentPage} / {numPages}
          </span>
          <button
            className={`dossier-page-nav-btn ${currentPage >= numPages ? "disabled-button" : ""}`}
            onClick={handleNextPage}
            disabled={currentPage >= numPages}
            aria-label="Next Page"
          >
            &#8250;
          </button>
        </div>
      )}
    </div>
  );
};

export const DossierPreview = ({
  isLoading = false,
  pdfUrl = null,
  docxBlob = null,
  mainFileName = "",
  applications = [],
  handleDownloadPdf,
  handleDownloadDocs,
  onAppSelect,
}) => {
  const { dashboard, global } = useConstants();

  const [selectedAppIndex, setSelectedAppIndex] = useState(0);

  // Auto-preview first app when ZIP files are loaded (and no global pdfUrl is present)
  useEffect(() => {
    if (!pdfUrl && applications.length > 0 && onAppSelect) {
      setSelectedAppIndex(0);
      onAppSelect(applications[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applications]);

  const hasAnyFile = !!pdfUrl || !!docxBlob || applications.some((a) => !!a.blob);
  const isDownloadDisabled = isLoading || !hasAnyFile;

  const handleSingleAppDownload = (e, app) => {
    e.stopPropagation();
    if (isLoading) return;
    if (app?.blob) {
      if (handleDownloadPdf) handleDownloadPdf(app);
    } else if (handleDownloadPdf) {
      handleDownloadPdf(app);
    }
  };

  const selectedApp = applications[selectedAppIndex] || null;
  const docxName = selectedApp?.name || mainFileName || "Generated_Dossier.docx";
  const docxSizeKb = docxBlob ? Math.round(docxBlob.size / 1024) : selectedApp?.size ? Math.round(selectedApp.size / 1024) : null;

  return (
    <div className="dossier-preview-layout">
      {/* ── Left 70% Container: Applications & Document Viewer ── */}
      <div className="dossier-main-card">
        {/* Full-width light blue bar */}
        <div className="dossier-header-bar">
          <img src={fileIcon} alt="" className="dossier-header-bar-icon" />
          <span className="dossier-header-bar-title">
            {dashboard.APPLICATIONS || "Applications"}{!isLoading && ` (${applications.length})`}
          </span>
        </div>

        {/* Content Body: Left App List + Right Document Preview */}
        <div className="dossier-card-body">
          {/* Applications list pane */}
          <div className="dossier-apps-sidebar">
            {isLoading ? (
              /* Skeleton for Application Rows */
              [1, 2, 3, 4].map((item) => (
                <div className="dossier-skeleton-row" key={item}>
                  <div className="dossier-skeleton-shimmer dossier-skeleton-pill" />
                  <div className="dossier-skeleton-shimmer dossier-skeleton-circle" />
                </div>
              ))
            ) : applications.length > 0 ? (
              /* Loaded Applications */
              applications.map((app, index) => {
                const isSelected = index === selectedAppIndex;
                return (
                  <div
                    key={app.id || index}
                    className={`dossier-app-item ${isSelected ? "dossier-app-item--active" : ""}`}
                    onClick={() => {
                      setSelectedAppIndex(index);
                      if (onAppSelect) onAppSelect(app);
                    }}
                  >
                    <span className="dossier-app-name" title={app.name || app.title}>
                      {app.name || app.title || `Application ${index + 1}`}
                    </span>
                    <div className="dossier-app-item-right">
                      {app.isPdf && <span className="dossier-file-badge dossier-file-badge--pdf">PDF</span>}
                      {app.isDocx && <span className="dossier-file-badge dossier-file-badge--docx">DOCX</span>}
                      <button
                        className={`dossier-app-download-btn ${isDownloadDisabled ? "disabled-button" : ""}`}
                        onClick={(e) => handleSingleAppDownload(e, app)}
                        disabled={isDownloadDisabled}
                        title={global.DOWNLOAD || "Download"}
                      >
                        <img src={downloadIcon} alt="Download" className="dossier-app-download-icon" />
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="dossier-no-apps">
                {global.NO_DATA_FOUND || "No applications found"}
              </div>
            )}
          </div>

          {/* Document Preview Pane */}
          <div className="dossier-doc-viewer">
            {isLoading ? (
              /* Skeleton for Document Preview */
              <div className="dossier-doc-skeleton-container">
                <div className="dossier-doc-skeleton-page">
                  <div className="dossier-skeleton-shimmer skeleton-doc-top-bar" />
                  <div className="dossier-skeleton-shimmer skeleton-doc-banner" />

                  <div className="skeleton-doc-group">
                    <div className="dossier-skeleton-shimmer skeleton-doc-line width-100" />
                    <div className="dossier-skeleton-shimmer skeleton-doc-line width-100" />
                    <div className="dossier-skeleton-shimmer skeleton-doc-line width-60" />
                  </div>

                  <div className="skeleton-doc-group">
                    <div className="dossier-skeleton-shimmer skeleton-doc-line width-100" />
                    <div className="dossier-skeleton-shimmer skeleton-doc-line width-50" />
                  </div>

                  <div className="skeleton-doc-group">
                    <div className="dossier-skeleton-shimmer skeleton-doc-line width-100" />
                    <div className="dossier-skeleton-shimmer skeleton-doc-line width-100" />
                    <div className="dossier-skeleton-shimmer skeleton-doc-line width-65" />
                  </div>

                  <div className="dossier-skeleton-shimmer skeleton-doc-footer-block" />
                </div>
              </div>
            ) : pdfUrl ? (
              /* Live PDF from API (global or per-app selected) */
              <LivePdfViewer key={pdfUrl} fileUrl={pdfUrl} />
            ) : selectedApp?.isDocx && selectedApp?.blob ? (
              /* ZIP: Selected app is a DOCX file */
              <div className="dossier-docx-preview-wrapper">
                <div className="dossier-docx-card">
                  <div className="dossier-docx-icon-wrapper">
                    <img src={fileIcon} alt="DOCX" className="dossier-docx-icon" />
                  </div>
                  <div className="dossier-docx-title">{selectedApp.name || selectedApp.fileName}</div>
                  <div className="dossier-docx-meta">
                    <span className="dossier-docx-badge">Word DOCX</span>
                    {selectedApp.size && <span>• {Math.round(selectedApp.size / 1024)} KB</span>}
                  </div>
                  <button
                    type="button"
                    className="button-style primary-button dossier-docx-download-btn"
                    onClick={(e) => handleSingleAppDownload(e, selectedApp)}
                  >
                    <img src={downloadIcon} alt="" className="dossier-btn-icon" />
                    Download DOCX
                  </button>
                </div>
              </div>
            ) : docxBlob ? (
              /* Global DOCX fallback */
              <div className="dossier-docx-preview-wrapper">
                <div className="dossier-docx-card">
                  <div className="dossier-docx-icon-wrapper">
                    <img src={fileIcon} alt="DOCX" className="dossier-docx-icon" />
                  </div>
                  <div className="dossier-docx-title">{docxName}</div>
                  <div className="dossier-docx-meta">
                    <span className="dossier-docx-badge">Word DOCX</span>
                    {docxSizeKb && <span>• {docxSizeKb} KB</span>}
                  </div>
                  <button
                    type="button"
                    className="button-style primary-button dossier-docx-download-btn"
                    onClick={handleDownloadDocs}
                  >
                    <img src={downloadIcon} alt="" className="dossier-btn-icon" />
                    Download DOCX
                  </button>
                </div>
              </div>
            ) : applications.length > 0 && !selectedApp?.blob ? (
              /* ZIP returned but first app not yet selected/previewed — prompt user to select */
              <div className="dossier-no-preview">
                <p>👆 Select a document from the list to preview it</p>
              </div>
            ) : (
              <div className="dossier-no-preview">
                <p>{global.NO_DATA_FOUND || "No document preview available"}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Right 30% Container: Action Buttons ── */}
      <div className="dossier-actions-sidebar">
        {/* Buttons Row */}
        <div className="dossier-buttons-row">
          <button
            className={`button-style primary-button dossier-download-action-btn ${isDownloadDisabled ? "disabled-button" : ""}`}
            onClick={handleDownloadPdf}
            disabled={isDownloadDisabled}
          >
            <img src={downloadIcon} alt="" className="dossier-btn-icon" />
            {dashboard.DOWNLOAD_PDF_ALL || "Download PDF (All)"}
          </button>

          <button
            className={`button-style primary-button dossier-download-action-btn ${isDownloadDisabled ? "disabled-button" : ""}`}
            onClick={handleDownloadDocs}
            disabled={isDownloadDisabled}
          >
            <img src={downloadIcon} alt="" className="dossier-btn-icon" />
            {dashboard.DOWNLOAD_DOCX_ALL || "Download Docx (All)"}
          </button>
        </div>

        {/* Prompt Manager Card (Disabled for now) */}
        <div
          className="prompt-manager-card prompt-manager-card--disabled"
          role="button"
          aria-disabled="true"
          title="Prompt Manager (Coming soon)"
        >
          <div className="prompt-manager-card-content">
            <img
              src={promptManagerIcon}
              alt=""
              className="prompt-manager-card-icon"
            />
            <span className="prompt-manager-card-label">
              {dashboard.PROMPT_MANAGER || "Prompt Manager"}
            </span>
          </div>
          <span className="prompt-manager-card-arrow">↗</span>
        </div>
      </div>
    </div>
  );
};

export default DossierPreview;
